import { css, keyframes } from '@emotion/css';

import { largeDesktopMediaQuery, nonMobileMediaQuery } from '../../constants';
import { h5Css, m, p2Css } from '../../motif';

export const editorialGalleryCardMediaClassName = 'editorial-gallery-card-media';

export const editorialGalleryCardClassName = 'editorial-gallery-card';

export const editorialGalleryCardContainerCss = css`
  align-items: center;
  background-color: ${m('--bg-color')};
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  width: 100%;

  &:hover {
    & .${editorialGalleryCardMediaClassName} {
      opacity: 1;
      transform: scale(1);
      z-index: 2;

      ${largeDesktopMediaQuery} {
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.12);
      }
    }
  }

  &::before {
    background-color: transparent;
    content: '';
    inset: 0 calc(${m('--spacing-xxxl')} * -1);
    position: absolute;

    ${largeDesktopMediaQuery} {
      width: calc(100% + 2 * ${m('--spacing-xxxl')});
    }
  }
`;

export const editorialGalleryCardLayoutCss = css`
  align-items: flex-start;
  align-self: stretch;
  color: ${m('--fg-color')};
  display: flex;
  justify-content: space-between;
  overflow: hidden;
  padding-block: ${m('--spacing-l')};
  /* Containing block for the stretched link ::after pseudo-element
     in stretchedLinkCss, allowing it to cover the full card area. */
  position: relative;

  ${nonMobileMediaQuery} {
    overflow: visible;
    padding-block: ${m('--spacing-xxl')};
  }
`;

/** Styles for the content section of the card, including the media and text. */
export const editorialGalleryCardContentCss = css`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${m('--spacing-l')};
  width: 100%;

  ${nonMobileMediaQuery} {
    flex-direction: row;
    gap: 72px;
  }
`;

/** Styles for the metadata section of the card, including the brand and date. */
export const editorialGalleryCardMetadataCss = css`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  justify-content: space-between;
  max-width: 100%;
  text-transform: uppercase;
  width: 100%;

  ${nonMobileMediaQuery} {
    flex-direction: column;
    max-width: 100px;
  }

  & > p {
    margin: 0;
  }
`;

export const editorialGalleryCardPreviewCss = css`
  display: flex;
  flex-direction: row-reverse;
  gap: ${m('--spacing-m')};
  width: 100%;

  ${nonMobileMediaQuery} {
    flex-direction: row;
    gap: 72px;
  }
`;

export const editorialGalleryCardAnnotationCss = css`
  font-size: ${m('--annotation-mobile-font-size')};
  font-weight: ${m('--annotation-mobile-font-weight')};
  letter-spacing: ${m('--annotation-mobile-font-letter-spacing')};
  line-height: ${m('--annotation-mobile-font-line-height')};
  margin: 0;
  margin-inline-start: auto;
  overflow-wrap: break-word;

  ${nonMobileMediaQuery} {
    font-size: ${m('--annotation-desktop-font-size')};
    font-weight: ${m('--annotation-desktop-font-weight')};
    letter-spacing: ${m('--annotation-desktop-font-letter-spacing')};
    line-height: ${m('--annotation-desktop-font-line-height')};
    margin-inline-start: 0;
  }
`;

export const editorialGalleryCardMediaContainerCss = css`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  justify-content: center;
  position: relative;
  width: 96px;

  ${nonMobileMediaQuery} {
    width: 126px;
  }

  ${largeDesktopMediaQuery} {
    width: 169px;
  }
`;

export const editorialGalleryCardMediaCss = css`
  aspect-ratio: 9/16;
  background-color: ${m('--bg-color')};
  border-radius: ${m('--border-radius-l')};
  opacity: 1;
  overflow: hidden;
  pointer-events: none;
  position: relative;
  top: 0;
  user-select: none;

  ${nonMobileMediaQuery} {
    inset: 0;
    position: absolute;
    z-index: 1;
  }

  ${largeDesktopMediaQuery} {
    transition: transform 0.25s ease-out;
    transform: scale(0.7);
  }
`;

export const editorialGalleryCardBodyCss = css`
  display: flex;
  flex-direction: column;
  gap: ${m('--spacing-l')};
  width: 100%;

  ${nonMobileMediaQuery} {
    gap: ${m('--spacing-m')};
  }

  ${largeDesktopMediaQuery} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

export const editorialGalleryCardTextGroupCss = css`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${nonMobileMediaQuery} {
    height: 115px;
    width: 100%;
  }

  ${largeDesktopMediaQuery} {
    height: 151px;
    max-height: 151px;
    max-width: 480px;
  }
`;

export const editorialGalleryCardTitleCss = css`
  ${h5Css}
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  display: -webkit-box;
  margin: 0px;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;

  ${nonMobileMediaQuery} {
    -webkit-line-clamp: 2;
  }
`;

export const editorialGalleryCardDescriptionCss = css`
  ${p2Css}
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: none;
  margin: 0px;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;

  ${nonMobileMediaQuery} {
    display: -webkit-box;
  }
`;

export const editorialGalleryCardButtonCss = css`
  display: flex;
  flex-grow: 0;
  height: fit-content;
  max-width: fit-content;

  & svg {
    flex-shrink: 0;
    height: 24px;
    width: 24px;
  }
`;

export const snapBrandBackgroundCss = css`
  ${largeDesktopMediaQuery} {
    &:hover {
      &::before {
        background-color: ${m('--neutral-v200')};
      }
      & .${editorialGalleryCardClassName} {
        color: ${m('--neutral-v700')};
      }
    }
  }
`;

export const snapchatBrandBackgroundCss = css`
  ${largeDesktopMediaQuery} {
    &:hover {
      &::before {
        background: ${m('--palette-yellow-v100')};
      }
      & .${editorialGalleryCardClassName} {
        color: ${m('--neutral-v700')};
      }
    }
  }
`;

export const lensBrandBackgroundCss = css`
  ${largeDesktopMediaQuery} {
    &:hover {
      &::before {
        background: linear-gradient(
          90deg,
          rgba(255, 252, 0, 1) 25%,
          rgba(217, 173, 224, 1) 75%,
          rgba(142, 102, 209, 1) 100%
        );
      }
      & .${editorialGalleryCardClassName} {
        color: ${m('--neutral-v700')};
      }
    }
  }
`;

export const spectaclesBrandBackgroundCss = css`
  ${largeDesktopMediaQuery} {
    &:hover {
      &::before {
        background: ${m('--neutral-v700')};
      }
      & .${editorialGalleryCardClassName} {
        color: ${m('--neutral-v0')};
      }
    }
  }
`;

export const noBrandBackgroundCss = css`
  ${largeDesktopMediaQuery} {
    &:hover {
      &::before {
        background: ${m('--editorial-gallery-card-hover-background-color')};
      }
    }
  }
`;

/**
 * Stretched link pattern: makes the entire card clickable by expanding the button's ::after
 * pseudo-element to cover the nearest positioned ancestor. The button's default hover/active
 * transform is disabled because CSS transforms create a new containing block, which collapses
 * ::after to the button's bounds and causes a hover feedback loop (flicker).
 */
export const stretchedLinkCss = css`
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  &:hover:not([disabled]) {
    transform: none;
  }

  &:active:not([disabled]) {
    transform: none;
  }
`;

/** Skeleton styles for the editorial gallery card skeleton. */
export const editorialGalleryCardSkeletonCss = css`
  background-color: ${m('--neutral-v200')};
`;

export const skeletonAnimationCss = keyframes`
  0% {
    transform: translateX(-100%);
  }
  30% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

export const editorialGalleryCardSkeletonAnimationCss = css`
  border-radius: ${m('--border-radius-m')};
  overflow: hidden;
  position: relative;

  &::before {
    animation: ${skeletonAnimationCss} 2s infinite;
    background: linear-gradient(0.25turn, transparent, ${m('--bg-color')}, transparent);
    content: '';
    height: 100%;
    left: 0;
    opacity: 0.8;
    pointer-events: none;
    position: absolute;
    top: 0;
    transform: translateX(-100%);
    width: 100%;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
    }
  }
`;

export const editorialGalleryCardSkeletonDescriptionCss = css`
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding-block-end: 4px;

  ${nonMobileMediaQuery} {
    display: flex;
    height: 52px;
  }
`;

export const editorialGalleryCardSkeletonTitleLinesCss = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  height: 96px;
  width: 100%;
  padding-block: 2px;

  ${nonMobileMediaQuery} {
    height: 52px;
  }
`;

export const editorialGalleryCardTextGroupSkeletonCss = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  ${largeDesktopMediaQuery} {
    height: 151px;
    max-height: 151px;
    max-width: 480px;
  }
`;

export const editorialGalleryCardSkeletonMediaCss = css`
  aspect-ratio: 9 / 16;
  border-radius: ${m('--border-radius-l')};
  flex-shrink: 0;
  position: absolute;
  width: 96px;

  ${nonMobileMediaQuery} {
    width: 126px;
  }

  ${largeDesktopMediaQuery} {
    opacity: 0;
    width: 169px;
  }
`;

export const editorialGalleryCardMetadataSkeletonCss = css`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  gap: 8px;
  justify-content: space-between;
  max-width: 100%;
  padding-block: 4px;
  text-transform: uppercase;
  width: 100%;

  ${nonMobileMediaQuery} {
    flex-direction: column;
    max-width: 100px;
  }
`;

export const editorialGalleryCardBrandSkeletonCss = css`
  height: 12px;
  width: 60px;
`;

export const editorialGalleryCardBrandDateSkeletonCss = css`
  height: 12px;
  width: 75px;
`;

export const editorialGalleryCardSkeletonBarCss = css`
  height: var(--skeleton-bar-height);
  width: var(--skeleton-bar-width);
`;

export const editorialGalleryCardButtonSkeletonCss = css`
  border-radius: 64px;
  height: 45px;
  width: 170px;

  ${nonMobileMediaQuery} {
    height: 50px;
  }
`;

export const editorialGalleryCardsContainerCss = css`
  background-color: ${m('--fg-color')};
  border-block: ${m('--border-width-xs')} solid ${m('--fg-color')};
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: ${m('--spacing-xxxl')};
`;
